import { ChatWithShortInfo } from "@sms/shared/chat/chat.interface";

export const filterChatsByUpdateDate: (chats: ChatWithShortInfo[]) => ChatWithShortInfo[] = (
  chats: ChatWithShortInfo[]
) => {
  return chats.sort((prevChat, nextChat) => {
    return Number(new Date(nextChat.updated_at)) - Number(new Date(prevChat.updated_at));
  });
};

type SortOrder = "asc" | "desc";
export const mergeArrayAndSortByKey: <T extends { id: string }, K extends keyof T>(
  currentList: T[],
  updateList: T[],
  sortKey?: K,
  sortOrder?: SortOrder
) => T[] = <T extends { id: string }, K extends keyof T>(
  currentList: T[],
  updateList: T[],
  sortKey?: K,
  sortOrder: SortOrder = "asc"
) => {
  const mergedArray: T[] = [...currentList, ...updateList];

  if (sortKey) {
    mergedArray.sort((a, b) => {
      const comparison: number = a[sortKey] < b[sortKey] ? -1 : a[sortKey] > b[sortKey] ? 1 : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }

  return removeDuplicateItemsWithId<T>(mergedArray);
};

const removeDuplicateItemsWithId: <T extends { id: string }>(array: T[]) => T[] = <T extends { id: string }>(
  array: T[]
): T[] => {
  const uniqueItemsMap: Map<string, T> = new Map<string, T>();

  for (const item of array) {
    if (!uniqueItemsMap.has(item.id)) {
      uniqueItemsMap.set(item.id, item);
    }
  }
  return Array.from(uniqueItemsMap.values());
};

export const mergeChatList: (prevChats: ChatWithShortInfo[], nextChats: ChatWithShortInfo[]) => ChatWithShortInfo[] = (
  prevChats: ChatWithShortInfo[],
  nextChats: ChatWithShortInfo[]
) => {
  const mergedChats: ChatWithShortInfo[] = [...(prevChats ?? []), ...(nextChats ?? [])];

  const uniqueChats: ChatWithShortInfo[] = filterUniqueChats(mergedChats);
  return uniqueChats;
};

function filterUniqueChats(chats: ChatWithShortInfo[]): ChatWithShortInfo[] {
  const uniqueChats: Map<string, ChatWithShortInfo> = new Map<string, ChatWithShortInfo>();
  for (const chat of chats) {
    if (!uniqueChats.has(chat.id)) {
      uniqueChats.set(chat.id, chat);
    }
  }
  return Array.from(uniqueChats.values());
}
export const handleAddRowInList: <T extends { id: string }>(list: T[], row: T) => T[] = <T extends { id: string }>(
  list: T[],
  row: T
) => {
  const uniqueItemsMap: Map<string, T> = new Map<string, T>();

  for (const item of list) {
    if (!uniqueItemsMap.has(item.id)) {
      uniqueItemsMap.set(item.id, item);
    }
  }

  uniqueItemsMap.set(row.id, { ...(uniqueItemsMap.get(row.id) ?? {}), ...row });

  return Array.from(uniqueItemsMap.values());
};

export const handleDeleteRowInListById: <T extends { id: string }>(list: T[], id: string) => T[] = <
  T extends { id: string }
>(
  list: T[],
  id: string
) => {
  const uniqueItemsMap: Map<string, T> = new Map<string, T>();

  for (const item of list) {
    if (!uniqueItemsMap.has(item.id)) {
      uniqueItemsMap.set(item.id, item);
    }
  }

  uniqueItemsMap.delete(id);

  return Array.from(uniqueItemsMap.values());
};
